import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'shared/context/AuthContext';
import { App, Select, Flex, Input, Layout } from "antd";
import { Avatar, Card, theme } from 'antd';
import { EditOutlined, EllipsisOutlined, SettingOutlined } from '@ant-design/icons';
const { Meta } = Card;
/**
 * Конфигурация модулей для dashboard
 */
const MODULES = [
  {
    id: 'auth',
    title: 'Авторизация',
    description: 'Управление пользователями, ролями, правами доступа и сессиями',
    icon: 'Shield',
    color: 'from-blue-500 to-indigo-600',
    bgPattern: 'bg-blue-500/10',
    path: '/users',
    features: [
      { label: 'Пользователи', path: '/users', icon: 'Users' },
      { label: 'Роли и права', path: '/role', icon: 'Key' },
      { label: 'Сессии', path: '/session', icon: 'Activity' },
      { label: 'Профиль', path: '/profile', icon: 'User' },
    ],
    stats: { label: 'Активных сессий', value: '—' },
  },
  {
    id: 'content',
    title: 'Контент',
    description: 'Управление игровыми объектами, рецептами, зданиями и параметрами',
    icon: 'Package',
    color: 'from-emerald-500 to-teal-600',
    bgPattern: 'bg-emerald-500/10',
    path: '/content/items',
    features: [
      { label: 'Сущности', path: '/content/items', icon: 'Sword' },
      { label: 'Параметры', path: '/content/parameters', icon: 'FlaskConical' },
      { label: 'Проекты', path: '/projects/list', icon: 'Folder' },
      { label: 'Версии', path: '/projects/versions', icon: 'Layers' },
    ],
    stats: { label: 'Объектов', value: '—' },
  },
  {
    id: 'localization',
    title: 'Локализация',
    description: 'Управление переводами, языками и локализованным контентом',
    icon: 'Globe',
    color: 'from-purple-500 to-pink-600',
    bgPattern: 'bg-purple-500/10',
    path: '/localization',
    features: [
      { label: 'Переводы', path: '/localization', icon: 'Languages' },
    ],
    stats: { label: 'Языков', value: '—' },
  },
  {
    id: 'placeholder',
    title: 'Скоро',
    description: 'Новый модуль в разработке. Следите за обновлениями!',
    icon: 'Sparkles',
    color: 'from-gray-400 to-gray-500',
    bgPattern: 'bg-gray-500/10',
    path: '#',
    features: [],
    stats: null,
    disabled: true,
    placeholder: true,
  },
];

/**
 * Карточка модуля
 */
const AntModuleCard = ({ module, onNavigate }) => {
  const isDisabled = module.disabled;

  return (
    <Card
      className={`
      relative group rounded-2xl overflow-hidden
      bg-surface border border-secondary-200
      transition-all duration-300 ease-out
      ${isDisabled
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 cursor-pointer'
        }
    `}
      onClick={() => !isDisabled && onNavigate(module.path)}
      cover={
        <div className={`h-32 bg-gradient-to-br ${module.color} relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/20" />
            <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-white/10" />
          </div>

          {/* Icon */}
          <div className="absolute bottom-4 left-6">
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Icon name={module.icon} size={28} className="text-white" />
            </div>
          </div>

          {/* Disabled Badge */}
          {isDisabled && !module.placeholder && (
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/30 backdrop-blur-sm">
              <span className="text-xs text-white font-medium">Скоро</span>
            </div>
          )}

          {/* Placeholder Badge */}
          {module.placeholder && (
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/30 backdrop-blur-sm flex items-center gap-1.5">
              <Icon name="Clock" size={12} className="text-white" />
              <span className="text-xs text-white font-medium">В разработке</span>
            </div>
          )}
        </div>
      }
    >
      <Flex orientation='vertical' flex='flex' justify='space-between' className="flex-wrap">
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {module.title}
        </h3>
        <p className="text-sm text-text-secondary mb-4 line-clamp-2">
          {module.description}
        </p>

        {/* Features */}
        {module.features.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {module.features.slice(0, 4).map((feature) => (
              <button
                key={feature.path}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) onNavigate(feature.path);
                }}
                disabled={isDisabled}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                  transition-colors duration-150
                  ${isDisabled
                    ? 'bg-secondary-100 text-secondary-400'
                    : 'bg-secondary-100 text-text-secondary hover:bg-primary hover:text-white'
                  }
                `}
              >
                <Icon name={feature.icon} size={12} />
                {feature.label}
              </button>
            ))}
            {module.features.length > 4 && (
              <span className="px-3 py-1.5 text-xs text-text-tertiary">
                +{module.features.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        {module.stats && (
          // <div className={`flex items-center gap-2 pt-4 border-t border-secondary-100`}>
          <Flex orientation='horizontal' wrap="wrap" gap='large' align='center' justify='start' className="pt-4 border-t border-secondary-100 flex-wrap">
            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${module.color}`} />
            <span className="text-sm text-text-secondary">{module.stats.label}:</span>
            <span className="text-sm font-semibold text-text-primary">{module.stats.value}</span>
            {/* </div> */}
          </Flex>
        )}

        {/* Arrow */}
        {!isDisabled && (
          <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Icon name="ArrowRight" size={20} className="text-primary" />
          </div>
        )}
      </Flex>
    </Card>
  );
};

/**
 * Dashboard Page
 */
const Dashboard = () => {
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNavigate = (path) => {
    if (path && path !== '#') {
      navigate(path);
    }
  };

  return (
    <div style={{ borderRadius: borderRadiusLG, background: colorBgContainer, width: '100%' }} className="min-h-screen ">
      {/* Header */}
      <div className=" border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* <div className="flex items-start justify-between"> */}
          <Flex orientation='horizontal' flex='flex' gap='large' justify='space-between' align='start'>
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                Добро пожаловать{user?.name ? `, ${user.name}` : ''}! 👋
              </h1>
              <p className="text-text-secondary text-lg">
                Выберите модуль для начала работы
              </p>
            </div>

            {/* Quick Stats */}
            <Flex wrap="wrap" flex align='center' orientation='horizontal' gap='large'>
              <div className="text-right flex-wrap">
                <p className="text-sm text-text-tertiary">Роль</p>
                <p className="text-lg font-semibold text-text-primary capitalize">
                  {user?.role || '—'}
                </p>
              </div>
              <div className="w-px h-10 bg-secondary-200" />
              <div className="text-right flex-wrap">
                <p className="text-sm text-text-tertiary">Статус</p>
                <Flex orientation='horizontal' flex='flex' align='center' gap='small' wrap="wrap">
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-lg font-semibold text-success">Онлайн</span>
                </Flex>
              </div>
            </Flex>
          </Flex>
        </div>
      </div>

      {/* Modules Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MODULES.map((module) => (
            <AntModuleCard
              key={module.id}
              module={module}
              onNavigate={handleNavigate}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-text-tertiary">
            Admin Panel • v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
